import React, { useEffect, useState, createContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Step, Stepper } from 'react-form-stepper';
import PageTitle from '../../../layouts/PageTitle';
import Elements from '../Elements/Elements';
import Button from '../Elements/Button';
import ButtonSubmit from '../Elements/ButtonSubmit';
import useNavigationPrompt from '../../../hooks/useNavigationPrompt';

import { setNotificationMessage } from '../../../../utils/setNotificationMessage';
import { getStepError } from '../../../../utils/getStepError';
import { NOMINATION_STATUS } from '../../../../utils/constants';

export const Context = createContext();

const WizardWrapper = ({
  formName,
  activeMenu,
  motherMenu,
  handleSubmit,
  onSubmit,
  register,
  watch,
  setValue,
  resetField,
  errors,
  setError,
  clearErrors = undefined,
  control,
  formState,
  setFormState,
  formStatusId = 0,
  trigger,
  hideButtons,
  requireConfirmation = false,
  refereeReportData = null,
  isFormDirty = false,
  submitPending = false,
}) => {
  const [numberOfSteps, setNumberOfSteps] = useState(1);
  const [stepNumber, setStepNumber] = useState(0);
  const [stepHeading, setStepHeading] = useState('Home');
  const [infoMessage, setInfoMessage] = useState('');

  const [formFieldSet, setFormFieldSet] = useState([]);
  const [stepLabel, setStepLabel] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [visibleNotification, setVisibleNotification] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);

  const stepsArray = Array.from(Array(numberOfSteps).keys());
  const getLabelText = (currentStepNumber) => {
    const textLabel = stepLabel.filter(
      (item) => Object.keys(item)[0] === `${currentStepNumber}`,
    )[0];

    if (textLabel !== undefined) {
      return textLabel[currentStepNumber];
    }
  };

  const numberOfErrors = Object.values(errors).filter(
    (error) => error.type !== 'info',
  ).length;

  const filteredErrors = Object.fromEntries(
    Object.entries(errors).filter(([_, error]) => error?.type !== 'info'),
  );

  useEffect(() => {
    if (
      watch('nominationStatusTypeId') === undefined ||
      watch('nominationStatusTypeId') === null
    ) {
      setValue('nominationStatusTypeId', NOMINATION_STATUS.DRAFT);
    }

    if (formStatusId === NOMINATION_STATUS.DRAFT && numberOfErrors === 0) {
      if (
        parseInt(watch('nominationStatusTypeId')) ===
        NOMINATION_STATUS.SUBMITTED
      ) {
        setValue('nominationStatusTypeId', NOMINATION_STATUS.DRAFT);
      }
    }

    if (formName === 'nomination') {
      setInfoMessage(
        setNotificationMessage(
          isDraft,
          numberOfErrors,
          formStatusId,
          isSubmitted,
          visibleNotification,
          formName,
          watch,
          errors,
        ),
      );
    }
  }, [
    formName,
    visibleNotification,
    formStatusId,
    isDraft,
    isSubmitted,
    watch,
    setValue,
    errors,
    numberOfErrors,
  ]);

  useEffect(() => {
    if (!visibleNotification) return undefined;
    if (formStatusId !== NOMINATION_STATUS.DRAFT && formStatusId !== 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setIsDraft(false);
      setVisibleNotification(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, [visibleNotification, formStatusId]);

  // Tracks whether the user has actually interacted with the form. RHF's
  // isDirty flips to true after the draft loader runs setValue-s, even though
  // the user hasn't touched anything — so isDirty alone is not a reliable
  // "has unsaved changes" signal. Gate the navigation prompt on a real
  // keydown/pointerdown before firing.
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    if (hasUserInteracted) return undefined;
    if (!isFormDirty || isSubmitted) return undefined;

    const markInteracted = () => setHasUserInteracted(true);
    window.addEventListener('keydown', markInteracted, { once: true });
    window.addEventListener('pointerdown', markInteracted, { once: true });
    return () => {
      window.removeEventListener('keydown', markInteracted);
      window.removeEventListener('pointerdown', markInteracted);
    };
  }, [isFormDirty, isSubmitted, hasUserInteracted]);

  const navigationPrompt = useNavigationPrompt(
    isFormDirty && !isSubmitted && hasUserInteracted,
  );
  return (
    <>
      {navigationPrompt}
      <PageTitle
        activeMenu={activeMenu}
        motherMenu={motherMenu}
        notificationMessage={infoMessage}
      />

      <div className="row">
        <div className="col-xl-12 col-xxl-12">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">{stepHeading}</h4>
            </div>

            <div className="card-body">
              <div className="form-wizard">
                <div className="stepper-wrapper">
                  {stepsArray.length > 1 && (
                    <Stepper
                      className="nav-wizard stepper"
                      activeStep={stepNumber}
                      nonLinear
                    >
                      {stepsArray.map((itemStepNumber, index) => {
                        let className = 'nav-link-inactive';
                        if (
                          (index !== stepsArray.length - 1 &&
                            index === stepNumber) ||
                          (index === stepsArray.length - 1 &&
                            stepNumber === stepsArray.length - 1)
                        ) {
                          className = 'nav-link';
                        }

                        if (
                          index === stepsArray.length - 1 &&
                          stepNumber !== stepsArray.length - 1
                        ) {
                          className = 'nav-link-inactive-last-step';
                        }
                        return (
                          <Step
                            key={itemStepNumber}
                            className={`${className} ${
                              getStepError(
                                filteredErrors,
                                formFieldSet,
                              ).includes(itemStepNumber + 1)
                                ? 'nav-link-error'
                                : ''
                            }`}
                            label={getLabelText(itemStepNumber + 1)}
                            styleConfig={{
                              labelFontSize: '1rem',
                            }}
                            onClick={() => setStepNumber(itemStepNumber)}
                          >
                            {(formStatusId === NOMINATION_STATUS.DRAFT ||
                              isDraft) &&
                            !getStepError(
                              filteredErrors,
                              formFieldSet,
                            ).includes(itemStepNumber + 1) ? (
                              <i className="bi bi-check2" />
                            ) : (
                              ''
                            )}
                          </Step>
                        );
                      })}
                    </Stepper>
                  )}
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <Context.Provider
                    value={useMemo(
                      () => ({
                        formName,
                        register,
                        watch,
                        setValue,
                        resetField,
                        errors,
                        setError,
                        clearErrors,
                        control,
                        formState,
                        setFormState,
                        trigger,
                        refereeReportData,
                      }),
                      [
                        formName,
                        register,
                        watch,
                        setValue,
                        resetField,
                        errors,
                        setError,
                        clearErrors,
                        control,
                        formState,
                        setFormState,
                        trigger,
                        refereeReportData,
                      ],
                    )}
                  >
                    <Elements
                      formName={formName}
                      stepNumber={stepNumber + 1}
                      setStepHeading={setStepHeading}
                      setNumberOfSteps={setNumberOfSteps}
                      fieldsValidated={isDraft}
                      setFormFieldSet={setFormFieldSet}
                      setStepLabel={setStepLabel}
                      errors={errors}
                      fileUploading={fileUploading}
                      setFileUploading={setFileUploading}
                    />
                  </Context.Provider>

                  {stepsArray.length > 1 && (
                    <div className="text-end toolbar toolbar-bottom p-2 mt-xl-5">
                      <Button
                        cssClass="btn btn-dark sw-btn-prev me-1"
                        setStepNumber={setStepNumber}
                        stepNumber={stepNumber}
                        textContent="Prev"
                        scrollToTop
                        isDisabled={stepNumber === 0}
                      />

                      <Button
                        cssClass="btn btn-dark sw-btn-next ms-1"
                        setStepNumber={setStepNumber}
                        stepNumber={stepNumber}
                        textContent="Next"
                        scrollToTop
                        isDisabled={stepNumber >= numberOfSteps - 1}
                        next
                      />
                    </div>
                  )}

                  <div className="text-end toolbar toolbar-bottom p-2 mt-xl-5">
                    {stepsArray.length > 1 && (
                      <ButtonSubmit
                        formName={formName}
                        textContent="Save Draft"
                        cssClass="btn btn-secondary sw-btn-next ms-1 standard-width-button"
                        setFormStatus={setIsDraft}
                        setVisibleNotification={setVisibleNotification}
                        scrollToTop
                        isDraftButton
                        errors={errors}
                        handleSubmit={handleSubmit}
                        onSubmit={onSubmit}
                        numberOfErrors={numberOfErrors}
                        isHidden={hideButtons}
                        isDisabled={fileUploading}
                        watch={watch}
                      />
                    )}
                    <ButtonSubmit
                      formName={formName}
                      textContent={submitPending ? 'Submitting…' : 'Submit'}
                      cssClass="btn btn-success sw-btn-next ms-1 standard-width-button"
                      setFormStatus={setIsSubmitted}
                      setVisibleNotification={setVisibleNotification}
                      isDisabled={
                        fileUploading || submitPending || numberOfErrors !== 0
                      }
                      scrollToTop={stepsArray.length > 1}
                      errors={errors}
                      handleSubmit={handleSubmit}
                      onSubmit={onSubmit}
                      numberOfErrors={numberOfErrors}
                      isHidden={hideButtons}
                      setValue={setValue}
                      watch={watch}
                      requireConfirmation={requireConfirmation}
                    />
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

WizardWrapper.propTypes = {
  formName: PropTypes.string,
  handleSubmit: PropTypes.func,
  onSubmit: PropTypes.func,
  register: PropTypes.func,
  trigger: PropTypes.func,
  resetField: PropTypes.func,
  watch: PropTypes.func,
  setValue: PropTypes.func,
  errors: PropTypes.object,
  setError: PropTypes.func,
  clearErrors: PropTypes.func,
  control: PropTypes.object,
  activeMenu: PropTypes.string,
  motherMenu: PropTypes.string,
  formState: PropTypes.object,
  setFormState: PropTypes.func,
  formStatusId: PropTypes.number,
  hideButtons: PropTypes.bool,
  requireConfirmation: PropTypes.bool,
  refereeReportData: PropTypes.object,
  isFormDirty: PropTypes.bool,
  submitPending: PropTypes.bool,
};

export default WizardWrapper;
